import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

/* Scripts ---------------------------*/
import { chooseCalc } from './../../assets/data/selectData.js';
import Data20 from './../../assets/data/2020data.json';
import Data21 from './../../assets/data/2021data.json';
import Data22 from './../../assets/data/2022data.json';
import Data23 from './../../assets/data/2023data.json';
import Data24 from './../../assets/data/2024data.json';
import Data25 from './../../assets/data/2025data.json';
import Data26 from './../../assets/data/2026data.json';

/* Components ---------------------------*/
import CalculatorOutput from './CalculatorOutput.jsx';
import TaxSelector from './TaxSelector.jsx';

const TaxesCalculator = () => {

    // Following state holds controlled input data
    const [ inputValue, inputValueUpdate ] = useState();
    // Following state saves controlled input data to new value on button click
    const [ calcValue, calcValueUpdate ] = useState('0');
    // Following state saves year value, default is 2025
    const [ selectYear, selectYearUpdate ] = useState('2025');
    // Following state saves "location" value, default is Federal
    const [ selectLocation, selectLocationUpdate ] = useState('Federal');
    // Following state saves filing value, default is Single
    const [ selectFiling, selectFilingUpdate ] = useState('Single');
    // Following state saves if the calculate button has been clicked, default is false
    const [ buttonClick, buttonClickUpdate ] = useState(false);

    /*---------------------------
    | useEffect holds all event listeners.
    |
    | Current listeners are:
    | - a click event that updates button state to check if a button has been pressed before firing the warning message for invalid inputs.
    | - a keypress event that attaches a button click event to the input when the enter key is pressed.
    |
    | useEffect refreshes whenever inputValue is updated i.e. user starts typing in input field.
    | useEffect cleanup returns the button click state value back to false which rehides the warning message if it was triggered.
    ---------------------------*/

    useEffect(() => {
        // checkClick() updates buttonClick state to true
        const checkClick = () => {
            buttonClickUpdate(true);
        }
        // Generates btn constant and attaches it to button with Id submitBtn
        const btn = document.getElementById('submitBtn');
        // Checks for button before adding click event listener to submitBtn
        btn?.addEventListener('click', checkClick);
        // enterPress() takes enter keypress event and ties it submitBtn as a click
        const enterPress = (e) => {
            if(e.key === 'Enter') {
                e.preventDefault();
                document.getElementById('submitBtn').click();
            }
        }
        // Generates enter constant and attaches it to input with Id income
        const enter = document.getElementById('income');
        // Checks for input before adding keypress event listener to income
        enter?.addEventListener('keypress', enterPress);

        return () => { buttonClickUpdate(false); };
    }, [inputValue]);

    const onChange = (e) => {
        inputValueUpdate(e.target.value);
    }

    const onClick = () => {
        calcValueUpdate(inputValue);
    }

    const yearChange = (e) => {
        selectYearUpdate(e.target.value);
    }

    const locationChange = (e) => {
        selectLocationUpdate(e.target.value);
    }

    const filingChange = (e) => {
        selectFilingUpdate(e.target.value);
    }

    const selectValues = chooseCalc.flatMap((test) => {
        return [test.year, test.location, test.status];
    });

    const cleanInput = (input) => {
        let regex = /[^0-9\-.]/gi;
        if(input === undefined){
            return ;
        }
        return input.replace(regex, "");
    }

    const dataSelect = (year, location, filingStatus) => {
        switch(year) {
            case '2020':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data20.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data20.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                })];
                            case 'Married Filing Joint':
                                return [Data20.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data20.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data20.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data20.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data20.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data20.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2021':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data21.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data21.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data21.Federal.Married_Filing_Joint_and_Surviving_Spouse_with_Dependents.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.Federal.Married_Filing_Joint_and_Surviving_Spouse_with_Dependents];
                            case 'Unmarried Head of Household':
                                return [Data21.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Single':
                                return [Data21.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.State.California.Single];
                            case 'Married Filing Separate':
                                return [Data21.State.California.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.State.California.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data21.State.California.Married_Filing_Joint_and_Surviving_Spouse_with_Dependents.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.State.California.Married_Filing_Joint_and_Surviving_Spouse_with_Dependents];
                            case 'Unmarried Head of Household':
                                return [Data21.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data21.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2022':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data22.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data22.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data22.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data22.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data22.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data22.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data22.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data22.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2023':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data23.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data23.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data23.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data23.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data23.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data23.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data23.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2024':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data24.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data24.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data24.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data23.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data24.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data24.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data24.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data24.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data24.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2025':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data25.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data25.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data25.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data25.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data25.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data25.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data25.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data25.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
            case '2026':
                switch(location) {
                    case 'Federal':
                        switch(filingStatus){
                            case 'Single':
                                return [Data26.Federal.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.Federal.Single];
                            case 'Married Filing Separate':
                                return [Data26.Federal.Married_Filing_Separate.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.Federal.Married_Filing_Separate];
                            case 'Married Filing Joint':
                                return [Data26.Federal.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.Federal.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data26.Federal.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.Federal.Unmarried_Head_of_Household];
                        }
                        break;
                    case 'California':
                        switch(filingStatus){
                            case 'Married Filing Separate':
                            case 'Single':
                                return [Data26.State.California.Single.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.State.California.Single];
                            case 'Married Filing Joint':
                                return [Data26.State.California.Married_Filing_Jointly.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.State.California.Married_Filing_Jointly];
                            case 'Unmarried Head of Household':
                                return [Data26.State.California.Unmarried_Head_of_Household.data.map((values) => {
                                    return {over: values[1], baseTax: values[2], percent: values[4]};
                                }), Data26.State.California.Unmarried_Head_of_Household];
                        }
                        break;
                }
                break;
        }
    }


    const sendData = dataSelect(selectYear, selectLocation, selectFiling);

    return (
        <TaxesCalculatorStyled className='TaxesCalculator'>
            <TaxSelector userChoice={ selectValues[0] } onChange={ yearChange } selectState={ selectYear } />
            <TaxSelector userChoice={ selectValues[1] } onChange={ locationChange } selectState={ selectLocation } />
            <TaxSelector userChoice={ selectValues[2] } onChange={ filingChange } selectState={ selectFiling } />
            <div className='userField'>
                <input
                    label='Income'
                    id='income'
                    type='text'
                    autoComplete='off'
                    placeholder='Enter taxable income'
                    onChange={ onChange }
                />
                <div className={`warningblock ${buttonClick && (calcValue === undefined || cleanInput(calcValue) === "") ? 'Warn' : 'Hide'}`}>
                    <FontAwesomeIcon icon={faCircleExclamation} />
                    <p className={ buttonClick && (calcValue === undefined || cleanInput(calcValue) === "") ? 'Warn' : 'Hide' }>Please Enter a Valid Number!</p>
                </div>
                <div className={`warningblock ${sendData[1].data.length == 0 ? 'Warn' : 'Hide'}`}>
                    <FontAwesomeIcon icon={faCircleExclamation} />
                    <p>
                        We Currently Do Not Have { selectLocation } Data for the Year { selectYear }. Please Choose a Different Option!
                    </p>
                </div>
                <button type='submit' id='submitBtn' className='btn-primary' onClick={ onClick }>Calculate</button>
            </div>
            <CalculatorOutput inputValue={ cleanInput(calcValue) } selectedData={ sendData[0] } tableData={ sendData[1]} location={ selectLocation } />
            <p className="Disclaimer"><em>This calculator is a reference tool only and should never be used for tax advice.</em></p>
        </TaxesCalculatorStyled>
    );
}

export default TaxesCalculator;

const TaxesCalculatorStyled = styled.div`
    input {
        min-width: 275px;
        padding: 10px 15px 10px 20px;
        margin-bottom: 15px;
        border-radius: 25px;
        border: 1px solid black;
    }
    p{
        padding-left: 5px;
        margin: 0;
    }
    #submitBtn {
        width: 275px;
        margin: 15px;
        padding: 13px;
        border-radius: 25px;
        background-color: #2f80b7;
        border: none;
        color: white;
    }
    .userField {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }
    .Disclaimer {
        padding-top: 20px;
        text-align: center;
    }
    .warningblock {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 5px 0px;
    }
    .Hide {
            display: none;
    }
    .Warn {
        font-size: 20px;
        color: #EA5151;
        text-align: center;
    }
    @media (max-width: 767px) {
        .warningblock {
            flex-direction: column;
        }
    }
`